import { useIntl } from "react-intl";
import { FormattedMessage } from "react-intl";
import { useCoursesLoaderData, useProgramDetails } from "../coursesLoader.ts";
import {
  formatRemaining,
  getDaysRemaining,
  getHoursRemaining,
  getMinutesRemaining,
} from "../timeHelper.tsx";
import { useCallback, useEffect, useState } from "react";
import styles from "./ProgramBanner.module.scss";
import { clsx } from "clsx";
import AnalogCharacters from "common/core-ui/Analog/AnalogCharacters.tsx";
import useTermOptDeadline from "../useTermOptDeadline.tsx";
import ProgramSwitcher from "../ProgramSwitcher";
import useSwitchProgram from "../useSwitchProgram.tsx";
import { useLayoutData } from "../../layout/myCoursesLayoutLoader.ts";
import ProgramBannerButton from "./ProgramBannerButton.tsx";
import useVirtualStore from "../../../common/virtual-store/useVirtualStore.tsx";
import ProgramBannerAddAllButton from "./ProgramBannerAddAllButton.tsx";
import useTermStoreMetrics from "../useTermStoreMetrics.tsx";
import { ViewProgramDetailsEvent } from "../PrintStatusChangeToast.tsx";
import useProgramBannerDiagnostics from "./useProgramBannerDiagnostics.ts";

export default function ProgramBanner() {
  const intl = useIntl();
  const courseData = useCoursesLoaderData();
  const {
    config: {
      personalizedValueSheet: { optingBannerButtonLabel },
      showStudentChoiceCountdownBanner,
    },
    isInstructor,
  } = useLayoutData();
  const { currentTerm } = courseData;
  const {
    ea: { allowsOptOut, name: eaProgramName },
    scope,
    participating,
    name,
    description,
  } = useProgramDetails();
  const [daysRemaining, setDaysRemaining] = useState<number>();
  const [hoursRemaining, setHoursRemaining] = useState<number>();
  const [minutesRemaining, setMinutesRemaining] = useState<number>(23);
  const { optWindowOpen, optDeadline } = useTermOptDeadline();
  const [isProgramSwitcherOpen, setIsProgramSwitcherOpen] = useState(false);
  const { changeProgram, isProgramChangeProcessing } = useSwitchProgram();
  const { isVirtualStore } = useVirtualStore();
  const { requiredToPurchaseBaseCount } = useTermStoreMetrics();
  const hideForStore =
    isVirtualStore && requiredToPurchaseBaseCount === 0 && scope === "ia_only";
  const isStudentChoice = scope === null;
  const useTermStartOn =
    (isVirtualStore && scope === "ia_only") || isStudentChoice;

  const handleViewProgramDetails = useCallback(
    (event: CustomEvent<ViewProgramDetailsEvent>) => {
      if (isProgramSwitcherOpen === false) {
        if (event?.detail?.termId === currentTerm?.id) {
          setIsProgramSwitcherOpen(true);
        } else {
          // ignore events for other terms for now
        }
      }
    },
    [currentTerm?.id, isProgramSwitcherOpen]
  );

  useEffect(() => {
    window.addEventListener(ViewProgramDetailsEvent, handleViewProgramDetails);

    return () => {
      window.removeEventListener(
        ViewProgramDetailsEvent,
        handleViewProgramDetails
      );
    };
  }, [handleViewProgramDetails]);

  const programAllowsBanner = () => {
    if (scope === "ea_only") {
      return allowsOptOut;
    }
    if (isStudentChoice) {
      return showStudentChoiceCountdownBanner;
    }
    return true;
  };
  const isDefinitelyALearner = courseData.courses.some(
    ({ userRole }) => userRole === "learner"
  );
  const showBanner =
    programAllowsBanner() &&
    optWindowOpen &&
    isDefinitelyALearner &&
    !hideForStore;

  useProgramBannerDiagnostics({
    programAllowsBanner: programAllowsBanner(),
    scope,
    allowsOptOut,
    showStudentChoiceCountdownBanner,
    optWindowOpen,
    termActive: currentTerm?.active,
    optDeadline,
    isDefinitelyALearner,
    isInstructor,
    hideForStore,
    isVirtualStore,
    requiredToPurchaseBaseCount,
    showBanner,
  });

  const deadline = useTermStartOn ? currentTerm?.startOn : optDeadline;

  const hasFutureDeadline = deadline && new Date() > new Date(deadline);
  const showCountdown =
    !hasFutureDeadline && daysRemaining !== undefined && daysRemaining <= 99;

  const updateRemaining = useCallback(() => {
    if (!deadline) {
      return;
    }
    setDaysRemaining(getDaysRemaining(deadline));
    setHoursRemaining(getHoursRemaining(deadline));
    setMinutesRemaining(getMinutesRemaining(deadline));
  }, [deadline]);

  useEffect(() => {
    updateRemaining();
    if (showCountdown) {
      const intervalId = setInterval(updateRemaining, 1000);
      return () => clearInterval(intervalId);
    }
  }, [showCountdown, updateRemaining]);

  const bannerTitle = () => {
    if (participating) {
      return intl.formatMessage(
        { defaultMessage: "You're enrolled in {program}" },
        { program: name }
      );
    }

    if (scope === "ea_only") {
      return intl.formatMessage(
        { defaultMessage: "{program} could save you money" },
        { program: eaProgramName }
      );
    }

    if (isStudentChoice) {
      if (daysRemaining === 0) {
        return intl.formatMessage({
          defaultMessage: "There's still time to buy your course materials",
        });
      }

      return intl.formatMessage({
        defaultMessage: "The countdown is on to get your course materials",
      });
    }

    return name;
  };

  const bannerDescription = () => {
    if (scope === "ea_only") {
      if (!participating) {
        return intl.formatMessage(
          {
            defaultMessage:
              "By opting back in to {program} you could save money on your course materials.",
          },
          { program: eaProgramName }
        );
      }
    }

    if (isStudentChoice) {
      return intl.formatMessage({
        defaultMessage:
          "You’ll be the most successful if you have your course materials on the first day of class.",
      });
    }

    return description;
  };

  const getBannerButton = () => {
    if (scope === "flexible") {
      return (
        <div className={styles.bannerButtonContainer}>
          <ProgramBannerButton
            data-testid="change-programs"
            onClick={() => setIsProgramSwitcherOpen(true)}
          >
            {optingBannerButtonLabel || (
              <FormattedMessage defaultMessage="View Program Options" />
            )}
          </ProgramBannerButton>
          <ProgramBannerAddAllButton isLinkVariant={true} />
        </div>
      );
    } else if (scope === "ea_only") {
      return (
        <div className={styles.bannerButtonContainer}>
          <ProgramBannerButton
            data-testid="ea-change-program"
            onClick={() => {
              !participating
                ? changeProgram("equitable_access")
                : setIsProgramSwitcherOpen(true);
            }}
            disabled={isProgramChangeProcessing}
          >
            {!participating ? (
              <FormattedMessage defaultMessage="Opt Back In" />
            ) : (
              optingBannerButtonLabel || (
                <FormattedMessage defaultMessage="View Program Options" />
              )
            )}
          </ProgramBannerButton>
          <ProgramBannerAddAllButton isLinkVariant={true} />
        </div>
      );
    }

    return (
      <ProgramBannerAddAllButton
        wrapperClassName={styles.bannerButtonContainer}
      />
    );
  };

  const showTimer = (
    remainingTime: number,
    remainingTitle: JSX.Element,
    testId: string
  ) => {
    return (
      <div className={styles.remainingContainer} data-testid={testId}>
        <div className={styles.remainingTitle}>{remainingTitle}</div>
        <div className={styles.remaining}>
          <AnalogCharacters
            fontSize="3rem"
            characters={formatRemaining(remainingTime)}
            gap="0.2rem"
          />
        </div>
      </div>
    );
  };

  const showDays = () => {
    const remainingTitle = <FormattedMessage defaultMessage="Days" />;
    const testId = "days-remaining";
    if (daysRemaining === undefined) {
      return;
    }
    return (
      <>
        <div className={styles.remainingContainerSmall}>
          {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
          {daysRemaining} {remainingTitle},
        </div>
        {showTimer(daysRemaining, remainingTitle, testId)}
      </>
    );
  };

  const showHours = () => {
    const remainingTitle = <FormattedMessage defaultMessage="Hours" />;
    const testId = "hours-remaining";
    if (hoursRemaining === undefined) {
      return;
    }
    return (
      <>
        <div className={styles.remainingContainerSmall}>
          {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
          {hoursRemaining} {remainingTitle},
        </div>
        {showTimer(hoursRemaining, remainingTitle, testId)}
      </>
    );
  };

  const showMinutes = () => {
    const remainingTitle = <FormattedMessage defaultMessage="Minutes" />;
    const testId = "minutes-remaining";
    if (minutesRemaining === undefined) {
      return;
    }
    return (
      <>
        <div className={styles.remainingContainerSmall}>
          {minutesRemaining} {remainingTitle}
        </div>
        {showTimer(minutesRemaining, remainingTitle, testId)}
      </>
    );
  };

  const showCountdownLabel = () => {
    if (useTermStartOn) {
      return <FormattedMessage defaultMessage="until the term starts" />;
    } else if (isVirtualStore && scope === "ea_only") {
      return !participating ? (
        <FormattedMessage defaultMessage="remaining to opt in" />
      ) : (
        <FormattedMessage defaultMessage="remaining to make your course material decisions" />
      );
    } else {
      return daysRemaining ? (
        <FormattedMessage defaultMessage="remaining to make your course material decisions" />
      ) : (
        <FormattedMessage defaultMessage="Today is the last day to make your course material decisions" />
      );
    }
  };

  const bannerBtn = getBannerButton();

  return (
    <>
      {showBanner ? (
        <section className={styles.container} data-testid="program-banner">
          <div className={clsx(styles.banner, bannerBtn)}>
            {showCountdown && (
              <div
                data-testid="time-remaining"
                className={styles.timeRemainingContainer}
              >
                <div className={styles.timeRemainingCount}>
                  {showDays()}
                  {showHours()}
                  {showMinutes()}
                </div>
                <div className={styles.timeRemainingText}>
                  {showCountdownLabel()}
                </div>
              </div>
            )}
            <div className={styles.contentHolder}>
              <div className={styles.textContent}>
                <h1 className={clsx("template-h2", styles.header)}>
                  {bannerTitle()}
                </h1>
                <div className={styles.description}>{bannerDescription()}</div>
              </div>
              {bannerBtn}
            </div>
          </div>
        </section>
      ) : null}
      {isProgramSwitcherOpen && (
        <ProgramSwitcher
          onRequestClose={() => setIsProgramSwitcherOpen(false)}
        />
      )}
    </>
  );
}
